import { useMemo, useState } from 'react';
import { AlertService } from '../../../services/alert.service';
import Datatable from '../../../components/datatable/datatable-xl.component';
import TruncatedName from '../../../components/forms/truncated-name.component';
import Icon from '../../../components/forms/icon.component';

interface Props {
  rows: any[];
  isLoading?: boolean;
  isRoot?: boolean;
  isAdmin?: boolean;
  onDelete?: (id: string | number) => Promise<void>;
  /** false = usuario de solo lectura: se ocultan editar/borrar/menú */
  canWrite?: boolean;
  onEdit?: (row: any) => void;
  onDuplicate?: (row: any) => void;
  onToggleEnabled?: (row: any) => void;
}

export default function TableDialplan({
  rows,
  isLoading = false,
  onDelete,
  canWrite = true,
  onEdit,
  onDuplicate,
  onToggleEnabled
}: Props) {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const handleDeleteClick = async (r: any) => {
    const recordLabel =
      r.description && r.description !== 'Sin descripción'
        ? `"${r.description}" (ID: #${r.id})`
        : `con ID: #${r.id}`;

    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      `Estás a punto de eliminar el siguiente registro: ${recordLabel}. ¿Verifica el registro antes de eliminarlo?`,
      () => Promise.resolve(true),
      'Continuar'
    );

    if (!isConfirmed) return;

    if (onDelete) {
      await onDelete(r.id);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        name: 'ID / Dialplan',
        sortable: true,
        minWidth: '120px',
        selector: (r: any) => r.id,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <span className="fw-bolder fs-6 text-dark">ID: #{r.id}</span>
            <span className="badge badge-light-primary fw-bolder mt-1 align-self-start">
              DPID: {r.dpid}
            </span>
          </div>
        )
      },
      {
        name: 'Descripción / Atributo',
        sortable: true,
        minWidth: '220px',
        selector: (r: any) => r.description,
        cell: (r: any) => (
          <div className="d-flex flex-column py-1 w-100">
            <TruncatedName
              text={r.description}
              maxWidth={200}
              className="fw-bold fs-7 text-gray-800"
            />
            {r.attrs && r.attrs !== '—' ? (
              <div className="mt-1">
                <TruncatedName
                  text={`Attrs: ${r.attrs}`}
                  maxWidth={200}
                  className="text-muted fs-9 d-block text-truncate"
                />
              </div>
            ) : (
              <span className="text-muted fs-9 mt-1">Sin Atributos</span>
            )}
          </div>
        )
      },
      {
        name: 'Expresiones RegExp',
        sortable: false,
        minWidth: '240px',
        cell: (r: any) => (
          <div className="d-flex flex-column py-1 text-monospace w-100">
            <div className="mb-1 text-truncate" style={{ maxWidth: '230px' }}>
              <span className="text-muted fs-9 fw-bold text-uppercase">Match ({r.matchOp}):</span>{' '}
              <code className="fs-8 px-1 py-0 bg-light text-dark rounded">{r.matchExp}</code>
            </div>
            <div className="text-truncate" style={{ maxWidth: '230px' }}>
              <span className="text-muted fs-9 fw-bold text-uppercase">Subst:</span>{' '}
              <code className="fs-8 px-1 py-0 bg-light text-danger rounded">{r.substExp}</code>
            </div>
          </div>
        )
      },
      {
        name: 'Reemplazo (Repl)',
        sortable: true,
        minWidth: '110px',
        selector: (r: any) => r.replExp,
        cell: (r: any) => {
          const visualRepl = String(r.replExp).replace(/\\/g, '\\\\');

          return (
            <code className="fs-8 px-2 py-1 bg-light text-primary rounded fw-bolder align-self-start">
              {visualRepl}
            </code>
          );
        }
      },
      {
        name: 'Estado',
        sortable: true,
        center: true,
        minWidth: '100px',
        selector: (r: any) => r.enabled,
        cell: (r: any) => (
          <span
            className={`badge ${r.enabled ? 'badge-light-primary text-primary' : 'badge-light-danger text-danger'} fw-bold fs-8`}
          >
            {r.enabled ? 'Activo' : 'Inactivo'}
          </span>
        )
      },
      {
        name: 'Acciones',
        center: true,
        minWidth: '160px',
        cell: (r: any) => {
          if (!canWrite) return <span className="text-muted fs-8">solo lectura</span>;
          const isDropdownOpen = openDropdownId === r.id;

          return (
            <div className="d-flex align-items-center gap-2 position-relative">
              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-primary btn-active-light-primary w-30px h-30px rounded-3"
                title="Editar Regla"
                onClick={() => onEdit?.(r)}
              >
                <Icon icon="ki-pencil" size="fs-5" />
              </button>

              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-danger btn-active-light-danger w-30px h-30px rounded-3"
                title="Eliminar Regla"
                onClick={() => handleDeleteClick(r)}
              >
                <Icon icon="ki-trash" size="fs-5" />
              </button>

              <div className="dropdown">
                <button
                  type="button"
                  className={`btn btn-icon btn-bg-light btn-color-gray-600 btn-active-light-dark w-30px h-30px rounded-3 ${isDropdownOpen ? 'show' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(isDropdownOpen ? null : r.id);
                  }}
                >
                  <Icon icon="ki-dots-horizontal" size="fs-4" />
                </button>

                {isDropdownOpen && (
                  <>
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100"
                      style={{ zIndex: 99, background: 'transparent' }}
                      onClick={() => setOpenDropdownId(null)}
                    />
                    <div
                      className="dropdown-menu menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold w-200px py-3 show"
                      style={{
                        position: 'absolute',
                        inset: 'auto 0px 0px auto',
                        transform: 'translate3d(0px, 35px, 0px)',
                        zIndex: 100,
                        display: 'block'
                      }}
                    >
                      <div className="menu-item px-3">
                        <a
                          href="#toggle"
                          className={`menu-link px-3 fs-7 ${r.enabled ? 'text-warning' : 'text-success'}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onToggleEnabled?.(r);
                          }}
                        >
                          <Icon
                            icon={r.enabled ? 'ki-cross-circle' : 'ki-check-circle'}
                            size="fs-6 me-2"
                          />{' '}
                          {r.enabled ? 'Deshabilitar' : 'Habilitar'}
                        </a>
                      </div>
                      <div className="menu-item px-3">
                        <a
                          href="#duplicate"
                          className="menu-link px-3 fs-7 text-info"
                          onClick={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onDuplicate?.(r);
                          }}
                        >
                          <Icon icon="ki-copy" size="fs-6 me-2" /> Duplicar
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }
      }
    ];
  }, [openDropdownId, canWrite, onEdit, onDuplicate, onToggleEnabled]);

  return <Datatable key="dialplan-table" columns={columns} data={rows} isLoading={isLoading} />;
}
